import { useId, useMemo } from 'react';

import type { OpenFilter, Prospect, SectorGroup } from '../../types';
import { applyLibraryFilters } from '../../lib/filters';
import { formatRating } from '../../lib/format';
import { getOpenStatus } from '../../lib/openingHours';
import { accentuate } from '../../lib/accents';
import { openStatusLabel } from '../lib/labels';
import { countdownLabel, dayShort, daysUntil, readTime } from '../lib/rdv';
import { CupMark, IconStar, IconTrash } from '../components/Icons';
import { Owl } from '../components/Owl';
import { LibraryTools } from '../components/LibraryTools';

/** Le sélecteur interne : les deux moitiés de la bibliothèque. */
export type LibraryPane = 'saved' | 'appointments';

export interface LibraryFilters {
  /** « all », « G:<groupe> » ou « S:<métier> ». */
  sector: string;
  openFilter: OpenFilter;
}

export const INITIAL_LIBRARY_FILTERS: LibraryFilters = {
  sector: 'all',
  openFilter: 'all',
};

interface LibraryScreenProps {
  pane: LibraryPane;
  onPaneChange: (pane: LibraryPane) => void;
  saved: readonly Prospect[];
  appointments: readonly Prospect[];
  isLoading: boolean;
  error: string | null;
  filters: LibraryFilters;
  onFiltersChange: (filters: LibraryFilters) => void;
  onOpen: (prospectId: number) => void;
  onRetry: () => void;
  onChanged: () => void;
  /** Retire la fiche (volet « Enregistrés ») ou annule le RDV (volet « RDV »). */
  onDiscard: (prospect: Prospect) => void;
  busyProspectId: number | null;
  sectorGroups: readonly SectorGroup[];
  now: Date;
}

/**
 * La bibliothèque — un seul écran, deux volets.
 *
 * Les rendez-vous ne sont pas un autre lieu : ce sont les fiches qui ont une
 * date. Un onglet séparé obligeait à savoir d'avance dans lequel des deux
 * chercher une fiche dont on ne se rappelle plus le statut ; ici les deux
 * compteurs sont visibles en permanence et on bascule sans quitter l'écran.
 *
 * Aucun score affiché. Il a servi une fois — au moment de garder la fiche
 * depuis les résultats. Passé ce tri, il ne décide plus rien : ce qui reste à
 * décider, c'est qui on appelle aujourd'hui, et ça se lit au métier et à
 * l'horaire, pas à un chiffre sur quinze.
 */
export function LibraryScreen(props: LibraryScreenProps) {
  const sectorId = useId();

  const { pane, filters, onFiltersChange, now } = props;
  const isAppointments = pane === 'appointments';
  const source = isAppointments ? props.appointments : props.saved;

  const visible = useMemo(
    () =>
      applyLibraryFilters(source, {
        search: '',
        status: 'all',
        // Les rendez-vous se rangent par échéance — le prochain d'abord, c'est
        // tout ce qu'on leur demande. Les enregistrés se rangent par score
        // avant d'être regroupés par métier ci-dessous : à métier égal, la
        // meilleure affaire remonte, même si le chiffre n'est plus montré.
        sort: isAppointments ? 'follow-up' : 'score',
        sector: filters.sector,
        // Le volet des rendez-vous n'offre pas « Ouvert » : il ne doit donc pas
        // en subir un resté actif depuis l'autre volet. Un filtre invisible qui
        // continue de retrancher des lignes est pire qu'un filtre en trop.
        openFilter: isAppointments ? 'all' : filters.openFilter,
        now,
      }),
    [source, filters, isAppointments, now],
  );

  /**
   * Regroupement par métier, sur le volet « Enregistrés » seulement.
   *
   * On prospecte par lots : quinze plombiers d'affilée, puis les coiffeurs. Un
   * classement par métier suit ce geste. Les rendez-vous, eux, restent
   * chronologiques — on veut savoir ce qui vient, pas de quel métier il est.
   */
  const groups = useMemo(() => {
    if (isAppointments) return [{ label: null, items: visible }];

    const byLabel = new Map<string, Prospect[]>();
    for (const prospect of visible) {
      const label =
        prospect.sectorLabel === null ? 'Non classé' : accentuate(prospect.sectorLabel);
      const bucket = byLabel.get(label);
      if (bucket === undefined) byLabel.set(label, [prospect]);
      else bucket.push(prospect);
    }

    return [...byLabel.entries()]
      .sort((a, b) =>
        // « Non classé » ferme la marche : ce n'est pas un métier.
        a[0] === 'Non classé' ? 1 : b[0] === 'Non classé' ? -1 : a[0].localeCompare(b[0], 'fr'),
      )
      .map(([label, items]) => ({ label, items }));
  }, [visible, isAppointments]);

  const patch = (next: Partial<LibraryFilters>) => onFiltersChange({ ...filters, ...next });

  return (
    <div className="stack">
      <div>
        <h1 className="title title-lg">Bibliothèque</h1>
        <p className="page-sub">
          {props.isLoading
            ? 'Chargement…'
            : `${props.saved.length} enregistré${props.saved.length > 1 ? 's' : ''} · ${props.appointments.length} rendez-vous`}
        </p>
      </div>

      {props.error !== null ? (
        <p className="notice notice-error" role="alert">
          {props.error}
          <button type="button" className="link" onClick={props.onRetry}>
            Réessayer
          </button>
        </p>
      ) : null}

      <div className="segment" role="group" aria-label="Volet de la bibliothèque">
        <button
          type="button"
          aria-pressed={!isAppointments}
          onClick={() => props.onPaneChange('saved')}
        >
          Enregistrés ({props.saved.length})
        </button>
        <button
          type="button"
          aria-pressed={isAppointments}
          onClick={() => props.onPaneChange('appointments')}
        >
          RDV fixés ({props.appointments.length})
        </button>
      </div>

      {/* Deux filtres, pas cinq. Le métier parce qu'on prospecte par lots, et
          « Ouvert » parce que c'est la seule chose qui décide si on décroche
          maintenant. Le reste se lit dans la liste. */}
      {source.length > 0 ? (
        <div className="lib-filters">
          <div className="field">
            <label className="sr-only" htmlFor={sectorId}>
              Métier
            </label>
            <select
              id={sectorId}
              className="select"
              value={filters.sector}
              onChange={(event) => patch({ sector: event.target.value })}
            >
              <option value="all">Tous métiers</option>
              {props.sectorGroups.map((group) => (
                <optgroup key={group.id} label={accentuate(group.label)}>
                  <option value={`G:${group.id}`}>Toute la catégorie</option>
                  {group.sectors.map((sector) => (
                    <option key={sector.id} value={`S:${sector.id}`}>
                      {accentuate(sector.label)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* « Ouvert » répond à « qui puis-je appeler maintenant ? ». C'est une
              question de prospection : sur un rendez-vous déjà fixé, savoir si
              la boutique est ouverte à cet instant ne décide de rien. */}
          {isAppointments ? null : (
            <button
              type="button"
              className="chip"
              aria-pressed={filters.openFilter === 'open'}
              onClick={() =>
                patch({ openFilter: filters.openFilter === 'open' ? 'all' : 'open' })
              }
            >
              Ouvert
            </button>
          )}
        </div>
      ) : null}

      {/* --- La liste ------------------------------------------------------ */}
      {props.isLoading ? (
        <p className="empty empty-text">Chargement…</p>
      ) : source.length === 0 ? (
        <div className="empty">
          {isAppointments ? <CupMark /> : <Owl quiet />}
          <p className="empty-title">
            {isAppointments ? 'Aucun rendez-vous' : 'Rien d’enregistré'}
          </p>
          <p className="empty-text">
            {isAppointments
              ? 'Fixez un rendez-vous depuis la fiche d’un prospect.'
              : 'Touchez le marque-page sur un prospect pour le garder ici.'}
          </p>
        </div>
      ) : visible.length === 0 ? (
        <div className="empty">
          <p className="empty-title">Aucune fiche pour ce filtre</p>
          <p className="empty-text">Retirez un filtre pour élargir la liste.</p>
        </div>
      ) : (
        groups.map((group) => (
          <section className="lib-group" key={group.label ?? 'toutes'}>
            {group.label === null ? null : (
              <h2 className="eyebrow lib-group-title">
                {group.label}
                <span className="lib-group-count">{group.items.length}</span>
              </h2>
            )}
            <ul className="rows rows-tight">
              {group.items.map((prospect) => (
                <LibraryRow
                  key={prospect.id}
                  prospect={prospect}
                  isAppointment={isAppointments}
                  busy={props.busyProspectId === prospect.id}
                  onOpen={props.onOpen}
                  onDiscard={props.onDiscard}
                  now={now}
                />
              ))}
            </ul>
          </section>
        ))
      )}

      {/* Sauvegarde, restauration, purge : de l'entretien, pas de la
          navigation. En bas, là où on ne va que délibérément — dans l'en-tête,
          trois libellés se disputaient la ligne du titre et la cassaient. */}
      <div className="lib-tools">
        <LibraryTools count={props.saved.length} onChanged={props.onChanged} />
      </div>
    </div>
  );
}

/* ========================================================================== */

function LibraryRow({
  prospect,
  isAppointment,
  busy,
  onOpen,
  onDiscard,
  now,
}: {
  prospect: Prospect;
  isAppointment: boolean;
  busy: boolean;
  onOpen: (id: number) => void;
  onDiscard: (prospect: Prospect) => void;
  now: Date;
}) {
  const open = getOpenStatus(prospect.openingHours, now);
  const day = prospect.nextFollowUpAt;
  const time = readTime(prospect.id);

  return (
    <li className="lib-row">
      <button
        type="button"
        className="lib-open"
        onClick={() => onOpen(prospect.id)}
        aria-label={`Ouvrir la fiche de ${prospect.name}`}
      >
        <span className="lib-name">{prospect.name}</span>
        <span className="lib-meta">
          {isAppointment ? (
            day === null ? (
              <span className="lib-when">Date à fixer</span>
            ) : (
              <>
                <span className="lib-when">
                  {dayShort(day)}
                  {time === null ? '' : ` · ${time}`}
                </span>
                <span>{countdownLabel(daysUntil(day, now))}</span>
              </>
            )
          ) : (
            <>
              {prospect.rating !== null ? (
                <span className="rating">
                  <IconStar size={11} />
                  {formatRating(prospect.rating)}
                </span>
              ) : null}
              <span className={`hours-${open.state.toLowerCase()}`}>
                {openStatusLabel(open)}
              </span>
            </>
          )}
        </span>
      </button>

      {/* Sur « RDV fixés » la corbeille annule le rendez-vous — la fiche
          retombe dans « Enregistrés ». Sur « Enregistrés » elle retire la
          fiche. Deux gestes différents sous la même icône, parce que dans les
          deux cas c'est « enlève ça d'ici ». */}
      <button
        type="button"
        className="btn-round btn-round-line lib-discard"
        onClick={() => onDiscard(prospect)}
        disabled={busy}
        aria-label={
          isAppointment
            ? `Annuler le rendez-vous de ${prospect.name}`
            : `Retirer ${prospect.name} de la bibliothèque`
        }
        title={isAppointment ? 'Annuler le rendez-vous' : 'Retirer de la bibliothèque'}
      >
        <IconTrash size={15} />
      </button>
    </li>
  );
}
